import { memo, useCallback } from "react";
import { useTranslate } from "helpers";
import {
  useHandleDateChange,
  useHandleDropdownChange,
  useHandleNumberFieldChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";
import DatePicker from "ui/components/DatePicker/DatePicker";
import { initialFleetVehicleExpenseSearchForm } from "services/api/fleet/vehicles/expenses/consts";
import VehicleManufacturerDropdown from "ui/components/Dropdown/VehicleManufacturerDropdown/VehicleManufacturerDropdown";
import VehicleModelDropdown from "ui/components/Dropdown/VehicleModelDropdown/VehicleModelDropdown";
import VehicleTypeDropdown from "ui/components/Dropdown/VehicleTypeDropdown/VehicleTypeDropdown";
import ExpenseTypeDropdown from "ui/components/Dropdown/ExpenseTypeDropdown/ExpenseTypeDropdown";
import { NumberField } from "ui/components/TextField/NumberField";
import { TextField } from "ui/components/TextField/TextField";
import { ExpenseSearchFiltersProps } from "./types";

const ExpenseSearchFilters: React.FC<ExpenseSearchFiltersProps> = (props) => {
  const { formValues, setFormValues } = props;

  const { t } = useTranslate();

  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);
  const handleDropdownChange = useHandleDropdownChange(setFormValues, formValues);
  const handleNumberFieldChange = useHandleNumberFieldChange(setFormValues, formValues);
  const handleDateChange = useHandleDateChange(setFormValues, formValues);

  // Kaskada: kad se promijeni gornja razina, niže razine se moraju resetirati
  // jer su njihove vrijednosti nevažeće za novi parent (drugi modeli/tipovi).
  const handleManufacturerChange = useCallback(
    (e: any, name: string, item: any) => {
      handleDropdownChange(e, name, item);
      setFormValues((prev) => ({
        ...prev,
        vehicleModelId: null,
        vehicleTypeId: null,
      }));
    },
    [handleDropdownChange, setFormValues]
  );

  const handleModelChange = useCallback(
    (e: any, name: string, item: any) => {
      handleDropdownChange(e, name, item);
      setFormValues((prev) => ({
        ...prev,
        vehicleTypeId: null,
      }));
    },
    [handleDropdownChange, setFormValues]
  );

  const handleResetFormClick = useCallback(() => {
    setFormValues(initialFleetVehicleExpenseSearchForm);
  }, [setFormValues]);

  return (
    <div className="flex flex-wrap items-end gap-2 mb-3">
      <div className="w-[150px]">
        <TextField
          type="text"
          name="search"
          value={formValues.search}
          onChange={handleTextFieldChange}
          placeholder={t("admin.search.placeholder")}
          startIcon="/media/icons/search.svg"
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[170px]">
        <VehicleManufacturerDropdown
          name="vehicleManufacturerId"
          value={formValues.vehicleManufacturerId}
          onChange={handleManufacturerChange}
          placeholder={t("admin.vehicle.manufacturer.placeholder")}
        />
      </div>
      <div className="w-[170px]">
        <VehicleModelDropdown
          name="vehicleModelId"
          vehicleManufacturerId={formValues.vehicleManufacturerId}
          value={formValues.vehicleModelId}
          onChange={handleModelChange}
          disabled={!formValues.vehicleManufacturerId}
          placeholder={t("admin.vehicle.model.placeholder")}
        />
      </div>
      <div className="w-[170px]">
        <VehicleTypeDropdown
          name="vehicleTypeId"
          vehicleManufacturerId={formValues.vehicleManufacturerId}
          vehicleModelId={formValues.vehicleModelId}
          value={formValues.vehicleTypeId}
          onChange={handleDropdownChange}
          disabled={!formValues.vehicleModelId}
          placeholder={t("admin.vehicle.type.placeholder")}
        />
      </div>
      <div className="w-[150px]">
        <ExpenseTypeDropdown
          name="expenseTypeId"
          value={formValues.expenseTypeId}
          onChange={handleDropdownChange}
          placeholder="Vrsta troška"
        />
      </div>
      <div className="w-[150px]">
        <DatePicker
          name="expenseDateFrom"
          value={formValues.expenseDateFrom}
          onChange={handleDateChange}
          placeholder="Datum od"
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[150px]">
        <DatePicker
          name="expenseDateTo"
          value={formValues.expenseDateTo}
          onChange={handleDateChange}
          placeholder="Datum do"
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[90px]">
        <NumberField
          name="amountMin"
          label="Iznos od - do"
          placeholder="Iznos od"
          value={formValues.amountMin}
          onChange={handleNumberFieldChange}
          decimals={2}
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[90px]">
        <NumberField
          name="amountMax"
          placeholder="do"
          value={formValues.amountMax}
          onChange={handleNumberFieldChange}
          decimals={2}
          cssClassName="bg-white"
        />
      </div>
      <span
        onClick={handleResetFormClick}
        className="text-xs font-bold cursor-pointer text-gray-500 hover:text-sky-600 pb-2 whitespace-nowrap"
      >
        {t("admin.reset")}
      </span>
    </div>
  );
};

export default memo(ExpenseSearchFilters);
